import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { identifyOneSignalUser, logoutOneSignalUser } from '../lib/onesignal';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on cross-origin requests (production: frontend on contratti.vitadahost.it
// calls backend on rental-sign-2.emergent.host). Combined with samesite=none/secure cookies
// from the backend, this ensures auth survives even if Bearer token is temporarily missing.
axios.defaults.withCredentials = true;

// 🌐 Production same-origin redirect:
// When the page is served from the user's custom domain (contratti.vitadahost.it),
// the deployed bundle still has REACT_APP_BACKEND_URL pointing to the Emergent default
// host (rental-sign-2.emergent.host). To avoid cross-origin cookie/CORS issues we rewrite
// ALL outgoing API calls to a relative path so that Emergent's ingress proxies /api/*
// to the backend on the SAME origin as the page. This is fully transparent.
const SAME_ORIGIN_HOSTS = ['contratti.vitadahost.it', 'www.contratti.vitadahost.it', 'locazioneturistica.com', 'www.locazioneturistica.com'];
axios.interceptors.request.use((config) => {
  try {
    if (typeof window !== 'undefined' && SAME_ORIGIN_HOSTS.includes(window.location.hostname)) {
      if (config.url && /^https?:\/\/[^/]*\.(emergent\.host|emergentagent\.com)/.test(config.url)) {
        config.url = config.url.replace(/^https?:\/\/[^/]+/, '');
      }
    }
  } catch (_) { /* noop */ }
  return config;
});

// Configure axios to send token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Link push subscription to this user (idempotent)
      identifyOneSignalUser(response.data);
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, ...userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    identifyOneSignalUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    logoutOneSignalUser();
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
